import React from "react";

function buildTruckIdList(rows = []) {
  return rows
    .map((row) => (typeof row?.assignmentId === "string" ? row.assignmentId : ""))
    .filter(Boolean);
}

function areSelectionsEqual(left = [], right = []) {
  if (left === right) {
    return true;
  }

  if (left.length !== right.length) {
    return false;
  }

  return left.every((truckId, index) => truckId === right[index]);
}

function normalizeSelection(selection, truckIdList) {
  if (!truckIdList.length) {
    return [];
  }

  if (!Array.isArray(selection) || !selection.length) {
    return [];
  }

  const requestedSelection = new Set(
    selection
      .map((truckId) => (typeof truckId === "string" ? truckId : ""))
      .filter(Boolean),
  );
  const nextSelection = truckIdList.filter((truckId) => requestedSelection.has(truckId));

  if (!nextSelection.length || nextSelection.length === truckIdList.length) {
    return [];
  }

  return nextSelection;
}

export default function useFleetTruckFilter({
  hideUnusedTrucks = false,
  initialSelection = null,
  onSelectionChange = null,
  rows = [],
}) {
  const triggerRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const truckIdList = React.useMemo(() => buildTruckIdList(rows), [rows]);
  const truckIdListKey = React.useMemo(() => truckIdList.join("::"), [truckIdList]);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [rawSelectedTruckIds, setRawSelectedTruckIds] = React.useState(() => normalizeSelection(initialSelection, truckIdList));
  const selectedTruckIds = React.useMemo(
    () => (rawSelectedTruckIds.length ? rawSelectedTruckIds : truckIdList),
    [rawSelectedTruckIds, truckIdList],
  );
  const selectedTruckIdsKey = React.useMemo(() => selectedTruckIds.join("::"), [selectedTruckIds]);
  const filterMessage = React.useMemo(
    () => (hideUnusedTrucks
      ? "Tous les camions sélectionnés sont actuellement masqués parce qu'aucune étape n'est planifiée."
      : "Aucun camion ne correspond au filtre courant. Ajustez la sélection pour remplir le Gantt."),
    [hideUnusedTrucks],
  );

  React.useEffect(() => {
    setRawSelectedTruckIds((currentSelection) => {
      if (!truckIdList.length) {
        return currentSelection.length ? [] : currentSelection;
      }

      if (!currentSelection.length) {
        return currentSelection;
      }

      const nextSelection = normalizeSelection(currentSelection, truckIdList);
      return areSelectionsEqual(currentSelection, nextSelection) ? currentSelection : nextSelection;
    });
  }, [truckIdListKey, truckIdList]);

  React.useEffect(() => {
    if (!isDropdownOpen || typeof document === "undefined") {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      const clickedTrigger = triggerRef.current instanceof HTMLElement && triggerRef.current.contains(target);
      const clickedPanel = panelRef.current instanceof HTMLElement && panelRef.current.contains(target);
      if (clickedTrigger || clickedPanel) {
        return;
      }

      setIsDropdownOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [isDropdownOpen]);

  React.useEffect(() => {
    onSelectionChange?.(selectedTruckIds);
  }, [onSelectionChange, selectedTruckIds, selectedTruckIdsKey]);

  const toggleDropdown = React.useCallback((nextValue) => {
    setIsDropdownOpen((currentValue) => {
      if (typeof nextValue === "function") {
        return Boolean(nextValue(currentValue));
      }

      if (typeof nextValue === "boolean") {
        return nextValue;
      }

      return !currentValue;
    });
  }, []);

  const setSelectedTruckIds = React.useCallback((nextSelection) => {
    setRawSelectedTruckIds((currentSelection) => {
      const resolvedCurrentSelection = currentSelection.length ? currentSelection : truckIdList;
      const requestedSelection = typeof nextSelection === "function"
        ? nextSelection(resolvedCurrentSelection)
        : nextSelection;
      const normalizedSelection = normalizeSelection(requestedSelection, truckIdList);

      return areSelectionsEqual(currentSelection, normalizedSelection) ? currentSelection : normalizedSelection;
    });
  }, [truckIdList]);

  const toggleTruckSelection = React.useCallback((assignmentId) => {
    if (!assignmentId) {
      return;
    }

    setRawSelectedTruckIds((currentSelection) => {
      const resolvedCurrentSelection = currentSelection.length ? currentSelection : truckIdList;
      const isSelected = resolvedCurrentSelection.includes(assignmentId);

      if (isSelected && resolvedCurrentSelection.length <= 1) {
        return currentSelection;
      }

      const nextSelection = isSelected
        ? resolvedCurrentSelection.filter((truckId) => truckId !== assignmentId)
        : truckIdList.filter((truckId) => truckId === assignmentId || resolvedCurrentSelection.includes(truckId));
      const normalizedSelection = normalizeSelection(nextSelection, truckIdList);

      return areSelectionsEqual(currentSelection, normalizedSelection) ? currentSelection : normalizedSelection;
    });
  }, [truckIdList]);

  const selectAllTrucks = React.useCallback(() => {
    setRawSelectedTruckIds([]);
  }, []);

  return {
    filterMessage,
    isDropdownOpen,
    panelRef,
    selectAllTrucks,
    selectedTruckIds,
    setSelectedTruckIds,
    toggleDropdown,
    toggleTruckSelection,
    triggerRef,
  };
}